import {
  ACTIONS,
  CHARTS,
  FIELD_TYPES,
  RESULT_TYPES,
} from "@/utils/calculatorSchema";

import {
  calculateLoanSummary,
  generateMonthlySchedule,
  getLoanYearBreakdown,
  getOldRegimeMarginalRate,
  getNewRegimeMarginalRate,
  getBreakdownChartData,
  formatCurrency,
} from "@/utils/finance";

import {
  ChartSpline,
  Receipt,
  Landmark,
  Percent,
  Wallet,
  Calendar,
  PiggyBank,
} from "lucide-react";

import DataTable from "@/components/calculator/DataTable";

import InsightCard from "@/components/calculator/InsightCard";

const taxBenefitCalculatorConfig = {
  id: "tax-benefit",

  shortTitle: "Tax Benefit",

  title: "Home Loan Tax Benefit Calculator",

  subtitle:
    "Estimate your income tax savings under the old and new tax regimes based on your home loan repayment.",

  icon: ChartSpline,

  description:
    "Calculate tax deductions available under Section 80C and Section 24(b), compare old vs new tax regimes, and estimate your annual tax savings from a home loan.",

  keywords: [
    "Home Loan Tax Benefit",
    "Section 80C",
    "Section 24B",
    "Tax Saving Calculator",
    "Old Tax Regime",
    "New Tax Regime",
    "Income Tax",
    "Home Loan Deduction",
  ],

  fields: [
    {
      key: "principal",
      label: "Loan Amount",
      type: FIELD_TYPES.CURRENCY,
      defaultValue: 5000000,
      min: 100000,
      max: 200000000,
      step: 100000,

      presets: [
        { label: "₹25L", value: 2500000 },
        { label: "₹50L", value: 5000000 },
        { label: "₹75L", value: 7500000 },
        { label: "₹1Cr", value: 10000000 },
        { label: "₹2Cr", value: 20000000 },
      ],
    },

    {
      key: "annualRate",
      label: "Interest Rate",
      type: FIELD_TYPES.PERCENT,
      defaultValue: 8.5,
      min: 1,
      max: 20,
      step: 0.05,
    },

    {
      key: "tenureYears",
      label: "Loan Tenure",
      suffix: "Years",
      type: FIELD_TYPES.NUMBER,
      defaultValue: 20,
      min: 5,
      max: 40,
      step: 1,
    },

    {
      key: "repaymentYear",
      label: "Repayment Year",
      suffix: "Year",
      type: FIELD_TYPES.NUMBER,
      defaultValue: 1,
      min: 1,
      max: 40,
      step: 1,
    },

    {
      key: "annualIncome",
      label: "Annual Taxable Income",
      helperText:
        "Enter your annual taxable income before claiming home loan deductions.",
      type: FIELD_TYPES.CURRENCY,
      defaultValue: 1200000,
      min: 0,
      max: 100000000,
      step: 100000,
    },

    {
      key: "taxRegime",
      label: "Tax Regime",
      type: FIELD_TYPES.SELECT,

      defaultValue: "old",

      options: [
        {
          label: "Old Regime",
          value: "old",
        },
        {
          label: "New Regime",
          value: "new",
        },
      ],
    },

    {
      key: "propertyStatus",
      label: "Property Status",
      type: FIELD_TYPES.SELECT,

      defaultValue: "self",

      options: [
        {
          label: "Self Occupied",
          value: "self",
        },
        {
          label: "Let Out",
          value: "let",
        },
      ],
    },
  ],

  summaryCards: [
    {
      key: "taxSaved",
      title: "Estimated Tax Saved",
      description: "Approximate annual income tax savings.",
      type: RESULT_TYPES.CURRENCY,
      icon: PiggyBank,
    },

    {
      key: "eligible80C",
      title: "80C Deduction",
      description: "Eligible principal deduction.",
      type: RESULT_TYPES.CURRENCY,
      icon: Landmark,
    },

    {
      key: "eligible24B",
      title: "24(b) Deduction",
      description: "Eligible interest deduction.",
      type: RESULT_TYPES.CURRENCY,
      icon: Receipt,
    },

    {
      key: "principalPaid",
      title: "Principal Paid",
      description: "Principal repaid in selected year.",
      type: RESULT_TYPES.CURRENCY,
      icon: Wallet,
    },

    {
      key: "interestPaid",
      title: "Interest Paid",
      description: "Interest paid in selected year.",
      type: RESULT_TYPES.CURRENCY,
      icon: Percent,
    },

    {
      key: "totalDeduction",
      title: "Total Deduction",
      description: "Total eligible deduction under Sections 80C and 24(b).",
      type: RESULT_TYPES.CURRENCY,
      icon: Landmark,
    },

    {
      key: "marginalRateDisplay",
      displayKey: "marginalRateText",
      title: "Tax Slab",
      description: "Applicable marginal tax rate.",
      type: RESULT_TYPES.TEXT,
      icon: Calendar,
    },
  ],

  charts: [
    {
      id: "deduction-breakdown",

      title: "Deduction Breakdown",

      type: CHARTS.PIE,

      resultsKey: "deductionBreakdown",

      dataKey: "value",

      nameKey: "name",

      innerRadius: 70,

      outerRadius: 110,

      visible: ({ values }) =>
        !(values.taxRegime === "new" && values.propertyStatus === "self"),
    },

    {
      id: "principal-interest",

      title: "Principal vs Interest",

      type: CHARTS.PIE,

      resultsKey: "principalInterest",

      dataKey: "value",

      nameKey: "name",

      innerRadius: 70,

      outerRadius: 110,
    },
  ],

  tables: [
    {
      title: "Monthly Schedule",

      component: DataTable,

      dataKey: "monthlySchedule",

      columns: [
        {
          key: "month",
          label: "Month",
          type: "number",
        },
        {
          key: "emi",
          label: "EMI",
          type: "currency",
        },
        {
          key: "principal",
          label: "Principal",
          type: "currency",
        },
        {
          key: "interest",
          label: "Interest",
          type: "currency",
        },
        {
          key: "balance",
          label: "Outstanding",
          type: "currency",
        },
      ],
    },

    {
      title: "Selected Year Summary",

      component: DataTable,

      dataKey: "yearSummary",

      columns: [
        {
          key: "label",
          label: "Particular",
          align: "left",
        },
        {
          key: "value",
          label: "Amount",
          type: RESULT_TYPES.CURRENCY,
        },
      ],
    },
  ],

  actions: [ACTIONS.RESET, ACTIONS.COPY, ACTIONS.EXCEL],

  calculate(values) {
    const {
      principal,
      annualRate,
      tenureYears,
      repaymentYear,
      annualIncome,
      taxRegime,
      propertyStatus,
    } = values;

    const options = {
      principal,
      annualRate,
      tenureYears,
    };

    // Base loan calculations
    const loanSummary = calculateLoanSummary(options);

    const monthlySchedule = generateMonthlySchedule(options);

    const selectedYear = Math.min(repaymentYear, tenureYears);

    const yearData = getLoanYearBreakdown(monthlySchedule, selectedYear);

    const principalPaid = yearData.principal;

    const interestPaid = yearData.interest;

    // Tax deductions
    const eligible80C =
      taxRegime === "old" ? Math.min(principalPaid, 150000) : 0;

    let eligible24B = 0;

    if (taxRegime === "old") {
      eligible24B =
        propertyStatus === "self"
          ? Math.min(interestPaid, 200000)
          : interestPaid;
    } else {
      eligible24B = propertyStatus === "let" ? interestPaid : 0;
    }

    // Tax slab
    const marginalRate =
      taxRegime === "old"
        ? getOldRegimeMarginalRate(annualIncome)
        : getNewRegimeMarginalRate(annualIncome);

    const effectiveRate = marginalRate * 1.04;

    const effectiveRateText = `${(effectiveRate * 100).toFixed(1)}%`;

    const taxSaved = (eligible80C + eligible24B) * effectiveRate;

    const totalDeduction = eligible80C + eligible24B;

    // Recommendation
    let recommendation = {};
    let benefitScore = 0;

    if (taxRegime === "old") {
      if (taxSaved >= 100000) {
        benefitScore = 100;

        recommendation = {
          title: "Excellent Tax Benefit",
          variant: "success",
          description:
            "Your home loan provides substantial tax savings through both Section 80C and Section 24(b).",
        };
      } else if (taxSaved >= 50000) {
        benefitScore = 75;

        recommendation = {
          title: "Good Tax Benefit",
          variant: "success",
          description:
            "Your home loan generates meaningful tax savings under the old tax regime.",
        };
      } else if (taxSaved >= 10000) {
        benefitScore = 50;

        recommendation = {
          title: "Moderate Tax Benefit",
          variant: "warning",
          description:
            "Tax deductions are available, although the overall savings are relatively modest.",
        };
      } else {
        benefitScore = 25;

        recommendation = {
          title: "Minimal Tax Benefit",
          variant: "destructive",
          description: "Your current loan generates only limited tax savings.",
        };
      }
    } else {
      if (propertyStatus === "let") {
        benefitScore = taxSaved >= 50000 ? 60 : 40;

        recommendation = {
          title: "Limited Tax Benefit",
          variant: "warning",
          description:
            "Section 80C isn't available under the new regime, but interest on a let-out property remains deductible under Section 24(b).",
        };
      } else {
        benefitScore = 0;

        recommendation = {
          title: "No Home Loan Tax Benefit",
          variant: "destructive",
          description:
            "The new tax regime doesn't provide home loan tax benefits for self-occupied properties.",
        };
      }
    }

    let taxNote;

    if (taxRegime === "old") {
      taxNote =
        "The old tax regime allows deductions under Sections 80C and 24(b). Tax savings are estimated using FY 2025–26 marginal tax slabs plus 4% health and education cess. Actual tax liability may differ depending on rebates, exemptions and other deductions.";
    } else if (propertyStatus === "let") {
      taxNote =
        "Under the new tax regime, Section 80C is unavailable. However, interest on a let-out property continues to qualify under Section 24(b) while computing house-property income. Loss set-off and carry-forward rules may apply.";
    } else {
      taxNote =
        "The new tax regime doesn't provide deductions under Section 80C or Section 24(b) for self-occupied residential properties, so home loan tax savings are generally unavailable.";
    }

    const yearSummary = [
      {
        label: "Principal Paid",
        value: principalPaid,
      },
      {
        label: "Interest Paid",
        value: interestPaid,
      },
      {
        label: "Eligible under Section 80C",
        value: eligible80C,
      },
      {
        label: "Eligible under Section 24(b)",
        value: eligible24B,
      },
      {
        label: "Total Eligible Deduction",
        value: totalDeduction,
      },
      {
        label: "Estimated Tax Saved",
        value: taxSaved,
      },
    ];

    const remainingDeduction = Math.max(totalDeduction - taxSaved, 0);

    return {
      summary: {
        emi: loanSummary.emi,

        principalPaid,

        interestPaid,

        eligible80C,

        eligible24B,

        totalDeduction,

        taxSaved,

        marginalRate,

        marginalRateText: `${Math.round(marginalRate * 100)}%`,

        effectiveRate,

        effectiveRateText,

        recommendation,

        benefitScore,

        taxNote,
      },

      charts: {
        deductionBreakdown: getBreakdownChartData({
          "80C Deduction": eligible80C,
          "24(b) Deduction": eligible24B,
        }),

        principalInterest: getBreakdownChartData({
          Principal: principalPaid,
          Interest: interestPaid,
        }),
      },

      tables: {
        monthlySchedule,

        yearSummary,
      },

      metadata: {
        repaymentYear: selectedYear,

        taxRegime,

        propertyStatus,

        annualIncome,

        marginalRate,

        effectiveRate,

        totalDeduction,
      },
    };
  },

  exportExcel: ({ results }) => ({
    "Monthly Schedule": results.tables.monthlySchedule ?? [],

    "Year Summary": results.tables.yearSummary ?? [],
  }),

  renderInsights({ values, results }) {
    const { taxRegime, propertyStatus } = values;

    const { repaymentYear } = results.metadata;

    const {
      taxSaved,
      eligible80C,
      eligible24B,
      totalDeduction,
      principalPaid,
      interestPaid,
      marginalRateText,
      effectiveRateText,
      recommendation,
    } = results.summary;

    return (
      <>
        <InsightCard
          variant={recommendation.variant}
          title={recommendation.title}
        >
          <p>{recommendation.description}</p>
        </InsightCard>

        <InsightCard title="Tax Benefit Summary" variant="info">
          <ul className="list-disc space-y-1 pl-5">
            <li>
              Estimated tax saving for repayment year{" "}
              <strong>{repaymentYear}</strong> is{" "}
              <strong>{formatCurrency(taxSaved)}</strong>.
            </li>

            <li>
              Effective tax rate including 4% cess is{" "}
              <strong>{effectiveRateText}</strong>.
            </li>

            <li>
              Your applicable marginal tax slab is{" "}
              <strong>{marginalRateText}</strong>.
            </li>

            <li>
              Tax calculations are based on the{" "}
              <strong>
                {taxRegime === "old" ? "Old Tax Regime" : "New Tax Regime"}
              </strong>
              .
            </li>
          </ul>
        </InsightCard>

        <InsightCard title="Deduction Breakdown" variant="success">
          <ul className="list-disc space-y-1 pl-5">
            <li>
              Section 80C deduction available:
              <strong> {formatCurrency(eligible80C)}</strong>
            </li>

            <li>
              Section 24(b) deduction available:
              <strong> {formatCurrency(eligible24B)}</strong>
            </li>

            <li>
              Total eligible deduction:
              <strong> {formatCurrency(totalDeduction)}</strong>
            </li>

            {taxRegime === "new" && (
              <li>
                The new tax regime does not allow Section 80C deductions for
                home loan principal repayment.
              </li>
            )}

            {propertyStatus === "self" && taxRegime === "old" && (
              <li>
                Interest deduction for self-occupied property is capped at
                ₹2,00,000 per financial year.
              </li>
            )}

            {propertyStatus === "let" && (
              <li>
                Interest paid on a let-out property is fully eligible under
                Section 24(b), subject to prevailing income tax provisions.
              </li>
            )}
          </ul>
        </InsightCard>

        <InsightCard
          title="Loan Repayment During Selected Year"
          variant="default"
        >
          <ul className="list-disc space-y-1 pl-5">
            <li>
              Principal repaid:
              <strong> {formatCurrency(principalPaid)}</strong>
            </li>

            <li>
              Interest paid:
              <strong> {formatCurrency(interestPaid)}</strong>
            </li>

            <li>
              Interest forms a larger portion of EMI during the initial years of
              a home loan, resulting in higher tax benefits under Section 24(b).
            </li>
          </ul>
        </InsightCard>

        <InsightCard title="Important Information" variant="warning">
          <p>{results.summary.taxNote}</p>
        </InsightCard>
      </>
    );
  },
};

export default taxBenefitCalculatorConfig;
